import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';

import { DonutSlice } from '../report.model';
import { ChartTheme, paletteFor } from './chart-palette';
import { sectorPath } from './chart-geometry';
import { ChartTooltipService } from './chart-tooltip.service';

/** Diamètre, rayons et écart angulaire — figés par la maquette de la carte. */
const SIZE = 208;
const INNER_R = 64;
const OUTER_R = 92;
const PADDING = 1.5;

/** Un secteur prêt à peindre. */
interface DonutArc {
  key: string;
  d: string;
  fill: string;
  value: number;
  label: string;
}

/**
 * Anneau d'acquisition des compétences.
 *
 * Il compte des compétences, pas des points : l'infobulle donne un effectif et
 * sa part, jamais une note. C'est la mesure « acquisition », distincte du score
 * du test qui vit dans la carte de résumé.
 *
 * Le composant ne rend **que** le SVG. `.donut__center` (taux au centre) et la
 * légende restent des frères dans le gabarit de la section : ce sont du texte
 * traduit et formaté, qui n'a rien à faire dans un SVG.
 */
@Component({
  selector: 's2e-donut-chart',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[style.display]': "'contents'" },
  template: `
    <svg
      class="chart-svg donut__svg"
      [attr.width]="size"
      [attr.height]="size"
      [attr.viewBox]="viewBox"
      role="img"
      [attr.aria-label]="ariaSummary() || null"
      focusable="false"
      (mouseleave)="leave()"
    >
      @for (arc of arcs(); track arc.key) {
        <path
          class="donut__slice"
          [attr.data-slice]="arc.key"
          [attr.d]="arc.d"
          [attr.fill]="arc.fill"
          [attr.stroke]="palette().surface"
          stroke-width="2"
          [attr.opacity]="opacityOf(arc.key)"
          (mouseenter)="enter(arc, $event)"
          (mousemove)="tooltip.move($event)"
        ></path>
      }
    </svg>
  `,
})
export class DonutChartComponent {
  readonly tooltip = inject(ChartTooltipService);

  readonly slices = input.required<readonly DonutSlice[]>();
  readonly theme = input.required<ChartTheme>();

  /**
   * Libellé accessible complet, déjà traduit et formaté par la section
   * (motif `chart.ariaDonut`). Le composant ne traduit rien : il ne connaît ni
   * la langue ni les effectifs mis en forme.
   */
  readonly ariaSummary = input('');

  /** Mise en forme d'un effectif ; remplacée par la section pour la locale. */
  readonly countFormat = input<(value: number) => string>((value) => String(value));
  /** Mise en forme d'une part ; idem. */
  readonly percentFormat = input<(value: number) => string>(
    (value) => `${Math.round(value)}%`,
  );

  readonly size = SIZE;
  readonly viewBox = `0 0 ${SIZE} ${SIZE}`;

  readonly palette = computed(() => paletteFor(this.theme()));

  /** Secteur survolé ; les autres sont estompés. */
  private readonly active = signal<string | null>(null);

  private readonly total = computed(() =>
    this.slices().reduce((sum, slice) => sum + slice.value, 0),
  );

  /**
   * Secteurs dessinables.
   *
   * Un secteur à zéro est retiré, **et** l'écart est recalculé sur ce qui
   * reste : garder sa place laisserait une encoche blanche inexpliquée dans un
   * anneau plein (cas « toutes les compétences acquises »).
   */
  readonly arcs = computed<DonutArc[]>(() => {
    const drawable = this.slices().filter((slice) => slice.value > 0);
    const total = this.total();
    if (drawable.length === 0 || total <= 0) return [];

    const gap = drawable.length > 1 ? PADDING : 0;
    const available = 360 - gap * drawable.length;
    const centre = SIZE / 2;
    let cursor = 90;

    return drawable.map((slice) => {
      const span = (slice.value / total) * available;
      const start = cursor;
      const end = cursor - span;
      cursor = end - gap;
      return {
        key: slice.key,
        d: sectorPath(centre, centre, INNER_R, OUTER_R, start, end),
        fill: slice.color,
        value: slice.value,
        label: slice.label,
      };
    });
  });

  /** `null` hors survol : le document d'origine ne pose pas l'attribut au repos. */
  opacityOf(key: string): string | null {
    const active = this.active();
    if (active === null) return null;
    return active === key ? '1' : '0.45';
  }

  enter(arc: DonutArc, event: MouseEvent): void {
    this.active.set(arc.key);
    const total = this.total();
    this.tooltip.show(
      arc.label,
      [
        {
          label: this.countFormat()(arc.value),
          value: this.percentFormat()(total > 0 ? (arc.value / total) * 100 : 0),
          color: arc.fill,
        },
      ],
      event,
    );
  }

  leave(): void {
    this.active.set(null);
    this.tooltip.hide();
  }
}
